<script setup>
import SmartBox from '../common/SmartBox.vue';
import DestinationSearch from './components/DestinationSearch.vue';
import Occupancy from './components/Occupancy.vue';
import SearchInput from './components/SearchInput.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import GlobalUtil from "../../utils/pricetravel/shared/globalUtil";
import { CalendarAvailability } from "../../utils/pricetravel/shared/calendarAvailability";
import GoogleMapList from "../../utils/maps/GoogleMapList";
import { storeToRefs } from 'pinia'
import { useHotelBookerStore } from '../stores/hotelBooker'
import { ref, getCurrentInstance, onMounted, computed, watch, nextTick } from 'vue'

const props = defineProps({
  isList: {
    type: String,
    default: 'false'
  },
  isDetail: {
    type: [String, Boolean],
    default: false
  },
  isSidebar: {
    type: [String, Boolean],
    default: false
  }
})

const emits = defineEmits(['searchDetail'])

const { proxy } = getCurrentInstance()
const __ = proxy.__

const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const _globalUtil = new GlobalUtil();
const defaultDate = fn.getDate();
const box = window.__pt.box || null;

const store = useHotelBookerStore()
const {
  form,
  fieldErrors,
  book,
  recentSearches,
  popularDestinations,
  searchResults,
  algoliaUrl,
  OpenSearch,
  CloseSearch,
  lastQuery,
  localeDatePicker,
  settingPaxes
} = storeToRefs(store)

const guestsBox = ref(null)
const calendarBox = ref(null);
const calendarPrices = ref({});
const isMobile = ref(false);
const isModalOpen = ref(false);
const summaryBtn = ref(null);
const modalCloseBtn = ref(null);

function closeModal() {
  isModalOpen.value = false;
}

// Focus management for the mobile "modify search" dialog (WCAG 2.4.3)
watch(isModalOpen, (open) => {
  nextTick(() => {
    if (open) {
      modalCloseBtn.value?.focus();
    } else {
      summaryBtn.value?.focus();
    }
  });
});

const isDetailMode = computed(() => {
  return props.isDetail === 'true' || props.isDetail === true;
});

const calendarDates = computed({
  get() {
    return {
      start: book.value.checkIn ? new Date(book.value.checkIn) : null,
      end: book.value.checkOut ? new Date(book.value.checkOut) : null
    };
  },
  set(val) {
    book.value.checkIn = val.start ? new Date(val.start) : null;
    book.value.checkOut = val.end ? new Date(val.end) : null;
    updateFormDates(book.value.checkIn, book.value.checkOut);
  }
});

const shortDates = computed(() => {
  if (!book.value.checkIn) return '';
  const format = localeDatePicker.value.formatMobile;
  const start = dayjs(book.value.checkIn).format(format);
  const end = book.value.checkOut ? dayjs(book.value.checkOut).format(format) : '';
  return end ? `${start} - ${end}` : start;
});

const shortGuests = computed(() => {
  const people = totalPeople.value;
  const rooms = book.value.paxes?.rooms || 1;
  const strPers = __('booker.pill_pers');
  const strHab = __('booker.pill_hab');
  return `${people} ${strPers} ${rooms} ${strHab}`;
});

function onSelectDates(val) {
  updateFormDates(val.start, val.end);
  book.value.checkIn = val.start;
  book.value.checkOut = val.end;
  if (calendarBox.value) {
    calendarBox.value.close();
  }
  
  if (val.start && val.end && isDetailMode.value) {
    isValidFormApply();
  }
}

function updateFormDates(start, end) {
  if (!start) return;
  const pathName = _globalUtil.getFirstPathSegment();
  const format =
    fn.mobileAndTabletCheck() && pathName == "hotel"
      ? localeDatePicker.value.formatMobile
      : localeDatePicker.value.format;

  if (start && end) {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')} - ${dayjs(end).format(format).replace(/\./g, '')}`;
  } else {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')}`;
  }
}

async function fetchAvailability(viewDate) {
  if (!config.showCalendarAvailability) return;
  const placeSelected = book.value?.place_selected;
  if (placeSelected && placeSelected.type == 14 && (placeSelected.code || placeSelected.id)) {
    const targetM = dayjs(viewDate);
    const year = targetM.format('YYYY');
    const month = targetM.format('MM');

    const response = await CalendarAvailability.getAvailability(
      placeSelected.code || placeSelected.id,
      year,
      month
    );
    if (response && response.daysByDate) {
      calendarPrices.value = { ...calendarPrices.value, ...extractPrices(response.daysByDate) };
    }
  }
}

function extractPrices(daysByDate) {
  const prices = {};
  for (const key in daysByDate) {
    const day = daysByDate[key];
    prices[day.checkinFormat] = {
      available: day.available,
      avgPrice: day.available ? (() => {
        const price = roundNumber(day.avgPrice);
        return price != 0 ? `$${price}` : '';
      })() : null,
      color: day.color
    };
  }
  return prices;
}
function roundNumber(val) {

  const currency = __pt.settings.site.currency;
  if (currency === 'COP') {
    const suffix = val.slice(-1);
    let number = parseFloat(val.slice(0, -1));
    return `${customRound(number)}${suffix}`;
  } else if (currency !== 'COP' && (val.endsWith('K') || val.endsWith('M'))) {
    return val;
  } else {
    return customRound(val);
  }
}

function customRound(num) {
  const decimal = num % 1;
  const integer = Math.floor(num);

  if (decimal > 0.5) {
    return integer + 1;
  } else {
    return num;
  }
}
function initDatePicker() {
  let paramsQuery = fn.search();
  if (
    (defaultDate < fn.getDate(book.value.checkOut) ||
      defaultDate >= fn.getDate(book.value.checkOut)) &&
    box
  ) {
    if (paramsQuery.daysToArrival && paramsQuery.daystostay) {
      let dateFirst = new Date();
      dateFirst.setDate(dateFirst.getDate() + parseInt(paramsQuery.daysToArrival));
      book.value.checkIn = dateFirst;
      let dateEnd = new Date(dateFirst);
      dateEnd.setDate(dateEnd.getDate() + parseInt(paramsQuery.daystostay));
      book.value.checkOut = dateEnd;
    } else {
      book.value.checkIn = fn.getDate(box.checkIn);
      book.value.checkOut = fn.getDate(box.checkOut);
    }
  }
  updateFormDates(book.value.checkIn, book.value.checkOut);
}

function onChangePaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
}
function onApplyPaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
  isValidFormApply();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}
function onCancelPaxes() {
  store.updateInputPaxes();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}
function onCloseGuests() {
  store.updateInputPaxes();
}

async function onSearch(value, mobile) {
  if (mobile) {
    form.value.destination = value;
  }
  await store.fetchSearchResults(value, mobile);
}

function onOpenSearch() {
  if (!window.__pt.settings.rb) {
    let mockVm = { ln: {} };
    let _googleMapNearby = new GoogleMapList(mockVm, window.axios, null, null);
    _googleMapNearby.getLocationUser();
  }

  setTimeout(() => {
    store.fetchPopularDestinations();
  }, 200);
}

function onSelectDestination(destination) {
  store.selectDestination(destination);
  store.setFieldError('destination', "");
  calendarPrices.value = {};
}
function isValidFormApply() {
  if (isDetailMode.value) {
    if (form.value.dates) {
      submitSearch();
    }
    return;
  }
  if (store.validateForm(true)) {
    submitSearch();
  }
}
function submitSearch() {
  if (isDetailMode.value) {
    if (!form.value.dates) {
      store.setFieldError('dates', __('booker.select_dates'));
      return;
    }
    const detailData = { 
      checkIn: book.value.checkIn, 
      checkOut: book.value.checkOut, 
      paxes: book.value.paxes 
    };
    emits('searchDetail', detailData);
    document.dispatchEvent(new CustomEvent('booker:searchDetail', { detail: detailData }));
    return;
  }
  store.submitSearch();
}

const hasData = computed(() => {
  return (searchResults.value?.length || 0) > 0 ||
    (recentSearches.value?.length || 0) > 0 ||
    (popularDestinations.value?.length || 0) > 0;
})

const totalPeople = computed(() => {
  if (!book.value || !book.value.paxes) return 0;

  let paxesData = book.value.paxes;
  if (typeof paxesData === 'string') {
    try { paxesData = JSON.parse(paxesData); } catch (e) { }
  }

  if (typeof paxesData === 'object' && !Array.isArray(paxesData)) {
    if ('adults' in paxesData) {
      paxesData = [paxesData];
    } else {
      paxesData = Object.values(paxesData);
    }
  }

  if (Array.isArray(paxesData)) {
    return paxesData.reduce((sum, room) => {
      return sum + (room.adults || 0) + (room.children ? room.children.length : 0);
    }, 0);
  }

  if (form.value && form.value.guests) {
    const aMatch = form.value.guests.match(/(\d+)\s*adulto/i);
    const cMatch = form.value.guests.match(/(\d+)\s*menor/i);
    return (aMatch ? parseInt(aMatch[1], 10) : 0) + (cMatch ? parseInt(cMatch[1], 10) : 0);
  }

  return 0;
});

onMounted(() => {
  store.initialDefaultDate();
  store.initSearchBox();
  initDatePicker();
  store.updateInputPaxes();
  store.loadSearchHistory();
})
</script>

<template>
  <div class="booker-wrapper"
    :class="{ 'is-list-view': isList === 'true' || isList === true, 'is-modal-open': isModalOpen, 'is-detail-view': isDetailMode, 'is-sidebar-view': isSidebar === 'true' || isSidebar === true }">
    <!-- Summary Card (Mobile Only) -->
    <button type="button" class="summary-card" ref="summaryBtn" @click="isModalOpen = true"
      :aria-label="__('booker.modify_search')">
      <span class="summary-card__box">
        <span class="summary-card__icon">
          <span class="icon font-icons icons-location font-24" aria-hidden="true"></span>
        </span>
        <span class="summary-card__data">
          <span class="summary-card__dest font-14 font-500 font-main">{{ form.destination }}</span>
          <span class="summary-card__info font-14 font-400 font-main text-disabled">
            {{ form.dates }} | {{ totalPeople }} {{ __('booker.persons') }}
          </span>
        </span>
      </span>
      <span class="summary-card__btn" aria-hidden="true">
        <span class="icon font-icons icons-search font-24"></span>
      </span>
    </button>

    <!-- Form Container -->
    <div class="booker-form-container">
      <div class="booker-modal__overlay" @click="isModalOpen = false" aria-hidden="true"></div>
      <div class="booker-modal__content" :role="isModalOpen ? 'dialog' : null"
        :aria-modal="isModalOpen ? 'true' : null" aria-labelledby="booker-modal-title"
        @keydown.esc="closeModal">

        <header class="booker-modal__header">
          <h2 id="booker-modal-title" class="font-16 font-500 font-main m-0">{{ __('booker.modify_search') }}</h2>
          <button type="button" class="booker-modal__close" ref="modalCloseBtn" @click="isModalOpen = false"
            :aria-label="__('messages.close')">
            <span class="icon font-icons icons-close font-24" aria-hidden="true"></span>
          </button>
        </header>

        <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.accommodations')">
          <header v-if="isSidebar === 'true' || isSidebar === true" class="sidebar-header d-none d-md-block">
            <h2 class="font-20 font-500 font-main m-0">{{ __('booker.modify_search') }}</h2>
          </header>

          <div class="search__inputs" :class="{'is-detail-mobile-pill-container': isDetailMode}">
            <SmartBox v-if="!isDetailMode" @open="onOpenSearch" @isMobile="isMobile = $event" :title="__('booker.where_are_you_going_sub')"
              :OpenBox="OpenSearch" :CloseBox="CloseSearch" :isHeaderActive="false" :hasContent="hasData">
              <template #trigger>
                <SearchInput id="dest" :label="__('booker.where_are_you_going')" :activeFocus="!isMobile"
                  :selectOnFocus="!isMobile" :placeholder="__('booker.destination_placeholder')"
                  v-model="form.destination" @input="onSearch" :debounce="700" :autocomplete="'off'" :spellcheck="false"
                  :error="!!fieldErrors.destination" :errorMessage="fieldErrors.destination" :readonly="isMobile">
                  <template #icon>
                    <span class="icon font-icons icons-location font-20" aria-hidden="true"></span>
                  </template>
                </SearchInput>
              </template>
              <template #content>
                <DestinationSearch :product="'H'" :recentSearches="recentSearches"
                  :popularDestinations="popularDestinations" @update:query="onSearch($event, true)"
                  :destination="form.destination" :imgUrl="algoliaUrl" :searchResults="searchResults"
                  @update:selectedDestination="onSelectDestination" :lastQuery="lastQuery"
                  :label="__('booker.where_are_you_going')" :placeholder="''" :icon="'icons-location'">
                </DestinationSearch>
              </template>
            </SmartBox>

            <SmartBox ref="calendarBox" :title="__('booker.dates')" :isHeaderActive="false" :mobile-breakpoint="768" :class="{'detail-mobile-smartbox': isDetailMode}">
              <template #trigger>
                <SearchInput class="d-none-detail-mobile" id="calendar-checkIn-hotel" type="text" required name="calendar-checkIn-hotel"
                  :activeFocus="true" :label="__('booker.dates')" :readonly="true" v-model="form.dates"
                  :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
                  <template #icon>
                    <span class="icon font-icons icons-calendar-depart font-20" aria-hidden="true"></span>
                  </template>
                </SearchInput>
                <div class="pill-half d-flex-detail-mobile" v-if="isDetailMode">
                  <span class="icon font-icons icons-calendar-depart font-20"></span>
                  <span class="pill-text">{{ shortDates }}</span>
                </div>
              </template>
              <template #content>
                <BookerCalendar :modelValue="calendarDates" :currency="config.currency" mode="range" :unit="'nights'"
                  :minDays="2" :nOffset="-1" :minDate="defaultDate" :prices="calendarPrices"
                  @viewDate="fetchAvailability" @select="onSelectDates" @close="calendarBox.close()" />
              </template>
            </SmartBox>

            <div class="pill-divider d-flex-detail-mobile" v-if="isDetailMode"></div>

            <SmartBox ref="guestsBox" :title="__('booker.guests_sub')" :isHeaderActive="false" @close="onCloseGuests"
              :mobile-breakpoint="768" :class="{'detail-mobile-smartbox': isDetailMode}">
              <template #trigger>
                <SearchInput class="d-none-detail-mobile" id="guests" :label="__('booker.guests')" :placeholder="form.guests" :readonly="true"
                  :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests"
                  :errorMessage="fieldErrors.guests">
                  <template #icon>
                    <span class="icon font-icons icons-guests font-20" aria-hidden="true"></span>
                  </template>
                </SearchInput>
                <div class="pill-half d-flex-detail-mobile" v-if="isDetailMode">
                  <span class="icon font-icons icons-guests font-20"></span>
                  <span class="pill-text">{{ shortGuests }}</span>
                </div>
              </template>
              <template #content>
                <Occupancy :product="'H'" :settingPaxes="settingPaxes" :counts="book.paxes" @change="onChangePaxes"
                  @apply="onApplyPaxes" @cancel="onCancelPaxes">
                </Occupancy>
              </template>
            </SmartBox>
          </div>

          <div class="search__action" :class="{'d-none-detail-mobile': isDetailMode}">
            <button type="submit" class="search__btn" :class="isDetailMode ? 'btnSecondary btnSecondary--md' : 'btnPrimary btnPrimary--md'">
              {{ isDetailMode ? __('booker.modify_search') : __('booker.button') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.search {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  padding: var(--space-16);
  border-radius: var(--space-12);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  background-color: var(--bg-base);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: auto;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    display: grid;
    grid-template-areas:
      'inputs inputs'
      'action action';
    grid-template-columns: 1fr 160px;
    padding-inline: 0px;
    gap: var(--space-24);
    box-shadow: none;
    margin: 0px auto;
    border: 0px;
  }

  @media (min-width: 1280px) {
    grid-template-areas:
      'inputs inputs action'
      'filters filters filters';
    width: 100%;
    margin: 0px;
    padding: var(--space-20);
    border-radius: var(--space-12);
    row-gap: 0px;
    column-gap: var(--space-32);
    border: 1px solid var(--border-subtle);
  }

  .icon {
    display: flex;
  }

  &__filters {
    grid-area: filters;
  }

  &__inputs {
    grid-area: inputs;
  }

  &__action {
    grid-area: action;

    @media (min-width: 768px) {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: minmax(240px, 2fr) minmax(200px, 1fr) minmax(200px, 1fr);
      gap: var(--space-32);
    }

    @media (min-width: 1280px) {
      grid-template-columns: 356px 1fr 1fr;
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 768px) {
      width: 100%;
    }

    @media (min-width: 1280px) {
      width: 160px;
    }
  }
}

.group {
  border: none;
  padding: 0px;
  margin: 0px;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-12);
  }

  &__legend {
    display: none;
    margin: 0px;

    @media (min-width: 768px) {
      display: flex;
      width: fit-content;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-12);
    margin: 0px;
    padding: 0px;
    list-style: none;
  }
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  margin: 0px;

}

.booker-wrapper {
  width: 100%;
  max-width: 1252px;
}

.booker-wrapper.is-list-view {
  @media (max-width: 767px) {
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
    background: var(--bg-base);
    border-bottom: none;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 10;

    .summary-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-16);
      cursor: pointer;
      width: 100%;
      max-width: 576px;
      background: none;
      border: none;
      padding: var(--space-8) var(--space-16);
      margin: 0 auto;
      text-align: left;
      font: inherit;
      color: inherit;

      &__box {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--space-8);
        padding: var(--space-4) var(--space-8);
        border: 1px solid var(--border-subtle);
        border-radius: var(--space-8);
        background: var(--bg-base);
        overflow: hidden;
      }

      &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        .icon {
          display: flex;
        }
      }

      &__data {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
      }

      &__dest {
        color: var(--text-main);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__info {
        color: var(--text-muted);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__btn {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        color: var(--bg-primary);
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
      }
    }

    &.is-modal-open .summary-card {
      display: none;
    }

    .booker-form-container {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.35s cubic-bezier(0.22, 1, 0.36, 1);
      max-width: 544px;
      margin: 0 auto;

      .booker-modal__overlay {
        display: none;
      }

      .booker-modal__content {
        overflow: hidden;
        min-height: 0;
        background-color: var(--bg-base);
        width: 100%;
        display: flex;
        flex-direction: column;
        opacity: 0;
        transition: opacity 0.12s ease;
      }

      .booker-modal__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-16) var(--space-16) 0px var(--space-16);

        h2 {
          font-family: var(--basefontFamily);
        }
      }

      .booker-modal__close {
        background: transparent;
        border: none;
        cursor: pointer;
        display: flex;
        color: var(--text-main);

        &:focus-visible {
          outline: 2px solid var(--border-focus, var(--bg-primary));
          outline-offset: 2px;
          border-radius: var(--space-4, 4px);
        }
      }

      .search {
        display: flex;
        flex-direction: column;
        box-shadow: none;
        border: none;
        margin: 0;
        width: 100%;

        .search__inputs {
          display: flex;
          flex-direction: column;
          gap: var(--space-16, 16px);
        }

        .search__action {
          display: flex;
          justify-content: stretch;

          .search__btn {
            width: 100%;
          }
        }
      }
    }

    &.is-modal-open .booker-form-container {
      grid-template-rows: 1fr;

      .booker-modal__content {
        opacity: 1;
        transition: opacity 0.2s ease 0.14s;
      }
    }

    @media (prefers-reduced-motion: reduce) {

      .booker-form-container,
      .booker-form-container .booker-modal__content {
        transition: none;
      }
    }
  }

  @media (min-width: 768px) {
    margin: 0;
    width: auto;
    position: static;
    transform: none;

    .summary-card {
      display: none;
    }

    .booker-form-container {
      display: block;
    }

    .booker-modal__overlay,
    .booker-modal__header {
      display: none;
    }
  }
}

.booker-wrapper:not(.is-list-view) {

  .summary-card,
  .booker-modal__overlay,
  .booker-modal__header {
    display: none;
  }
}

.booker-wrapper.is-detail-view {
  .booker-form-container {
    display: block;
  }

  .search {
    margin: 0;
    box-shadow: none;

    @media (max-width: 767px) {
      border: none;
      background-color: transparent;
      padding: 0;
    }

    @media (min-width: 768px) {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: space-between;
      gap: var(--space-16);
      width: 100%;
    }

    .search__inputs {
      @media (min-width: 768px) {
        flex: 1;
        display: flex;
        flex-direction: row;
        gap: var(--space-16);
        width: 100%;

        > * {
          flex: 1;
        }
      }
    }

    .search__action {
      @media (min-width: 768px) {
        flex: 0 0 auto;
        margin: 0;
        display: flex;
        align-items: stretch;

        .search__btn {
          width: auto;
          padding: 0 var(--space-32);
          min-width: 200px;
          height: auto;
          min-height: 56px;
        }
      }
    }
  }

  .d-flex-detail-mobile {
    display: none;
  }

  @media (max-width: 767px) {
    .d-none-detail-mobile {
      display: none;
    }

    .d-flex-detail-mobile {
      display: flex;
    }

    .is-detail-mobile-pill-container {
      flex-direction: row;
      align-items: stretch;
      border: 1px solid var(--border-subtle);
      background-color: var(--bg-base);
      border-radius: 8px;
      padding: 0;
      gap: 0;
      margin: 0 auto;
      width: 100%;
      height: 48px;
    }

    .detail-mobile-smartbox {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .pill-half {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--space-8);
      padding: 0 var(--space-8);
      font-size: 16px;
      color: var(--text-main);
      cursor: pointer;
      height: 100%;
      width: 100%;
      background: transparent;
      min-width: 0;
    }

    .pill-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .pill-divider {
      width: 1px;
      background-color: var(--border-subtle);
      margin: var(--space-8) 0;
    }
  }
}

.booker-wrapper.is-sidebar-view {
  .search {
    @media (min-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 0;
      border: 1px solid var(--border-subtle);
      border-radius: var(--space-12);

      .sidebar-header {
        padding-bottom: var(--space-16);
        width: 100%;
      }

      .search__inputs {
        display: flex;
        flex-direction: column;
        gap: var(--space-16);
      }

      .search__action {
        margin-top: var(--space-16);

        .search__btn {
          width: 100%;
        }
      }
    }
  }

  :deep(.field) {
    gap: var(--space-4);
    padding: 0px var(--space-8);
  }

  :deep(.field__label) {
    font-size: 0.688rem !important;
  }
}
</style>
